import React, { useState, useEffect } from 'react';
import TagInput from './TagInput';

export default function AddEditModal({ title, fields, initialData, onSave, onClose, loading }) {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        // Initialize form with initial data or defaults
        const initial = {};
        fields.forEach(field => {
            if (field.type === 'tags' || field.type === 'list') {
                initial[field.name] = initialData[field.name] || [];
            } else if (field.type === 'importance') {
                initial[field.name] = initialData[field.name] || 3;
            } else if (field.type === 'checkbox') {
                initial[field.name] = initialData[field.name] || false;
            } else {
                initial[field.name] = initialData[field.name] || '';
            }
        });
        setFormData(initial);
    }, [initialData, fields]);

    const handleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const renderField = (field) => {
        const value = formData[field.name];

        switch (field.type) {
            case 'textarea':
                return (
                    <textarea
                        className="form-textarea"
                        value={value || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        rows="3"
                    />
                );

            case 'select':
                return (
                    <select
                        className="form-select"
                        value={value || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                    >
                        <option value="">اختر...</option>
                        {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                );

            case 'checkbox':
                return (
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={value || false}
                            onChange={(e) => handleChange(field.name, e.target.checked)}
                        />
                        <span className="checkbox-text">{field.label}</span>
                    </label>
                );

            case 'tags':
                return (
                    <TagInput
                        tags={value || []}
                        onChange={(tags) => handleChange(field.name, tags)}
                        placeholder={field.placeholder}
                    />
                );

            case 'list':
                return (
                    <ListInput
                        items={value || []}
                        onChange={(items) => handleChange(field.name, items)}
                        placeholder={field.placeholder}
                    />
                );

            case 'importance':
                return (
                    <ImportanceSelector
                        value={value || 3}
                        onChange={(val) => handleChange(field.name, val)}
                    />
                );

            default:
                return (
                    <input
                        type={field.type || 'text'}
                        className="form-input"
                        value={value || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                    />
                );
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="modal-body">
                        <div className="form-grid">
                            {fields.filter(f => f.type !== 'checkbox').map(field => (
                                <div key={field.name} className={`form-group ${field.type === 'textarea' || field.type === 'list' || field.type === 'tags' ? 'full-width' : ''}`}>
                                    {field.type !== 'checkbox' && (
                                        <label className="form-label">
                                            {field.label}
                                            {field.required && <span className="required">*</span>}
                                        </label>
                                    )}
                                    {renderField(field)}
                                </div>
                            ))}
                            {fields.filter(f => f.type === 'checkbox').map(field => (
                                <div key={field.name} className="form-group">
                                    {renderField(field)}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="cancel-button" onClick={onClose}>
                            إلغاء
                        </button>
                        <button type="submit" className="save-button" disabled={loading}>
                            {loading ? 'جاري الحفظ...' : 'حفظ'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// List Input Component
function ListInput({ items, onChange, placeholder }) {
    const [inputValue, setInputValue] = useState('');

    const handleAdd = () => {
        if (inputValue.trim()) {
            onChange([...items, inputValue.trim()]);
            setInputValue('');
        }
    };

    const handleRemove = (index) => {
        onChange(items.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <div className="list-input">
            <div className="list-items">
                {items.map((item, index) => (
                    <div key={index} className="list-item">
                        <span className="list-item-text">{item}</span>
                        <button type="button" className="list-item-remove" onClick={() => handleRemove(index)}>
                            ×
                        </button>
                    </div>
                ))}
            </div>
            <div className="list-input-row">
                <input
                    type="text"
                    className="form-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                />
                <button type="button" className="list-add-button" onClick={handleAdd}>
                    إضافة
                </button>
            </div>
        </div>
    );
}

// Importance Selector Component
function ImportanceSelector({ value, onChange }) {
    return (
        <div className="importance-selector">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    className={`importance-star ${star <= value ? 'filled' : ''}`}
                    onClick={() => onChange(star)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill={star <= value ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                </button>
            ))}
            <span className="importance-label">
                {value === 1 && 'منخفض'}
                {value === 2 && 'أقل من المتوسط'}
                {value === 3 && 'متوسط'}
                {value === 4 && 'مرتفع'}
                {value === 5 && 'حرج'}
            </span>
        </div>
    );
}
